import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  PackageCheck,
  Plus,
  Radar,
  RefreshCw,
  Router,
  Trash2,
  Undo2,
} from "lucide-react";

import {
  api,
  ApiError,
  type Connector,
  type DiscoveredDevice,
  type EnrollmentBundle,
} from "../../api";
import { takePendingClaim } from "../../claim";
import { Modal } from "../../components/Modal";
import { SearchableSelect } from "../../components/SearchableSelect";
import { Select } from "../../components/Select";
import { useScope } from "../../components/ScopeContext";
import { emptyNvr, NvrDraftModal, useNvrUpsert, type NvrDraft } from "./NvrsSection";
import { BRAND_OPTIONS, EmptyState, Note, SettingsSection } from "./shared";

function relativeTime(iso: string | null): string {
  if (!iso) return "never";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
}

function statusOf(c: Connector): { label: string; tone: string } {
  if (c.online) return { label: "Online", tone: "text-emerald-300" };
  if (!c.store && c.registered) return { label: "Not plugged in", tone: "text-bf-muted" };
  if (!c.registered && c.enrollment_expires_at)
    return { label: "Waiting for device", tone: "text-amber-200" };
  if (!c.registered) return { label: "Not enrolled", tone: "text-bf-muted" };
  return { label: "Offline", tone: "text-red-300" };
}

const PATH_LABEL: Record<string, string> = {
  direct: "Direct link",
  relay: "Relayed link",
  idle: "Idle",
};

function recorderIdFor(device: DiscoveredDevice): string {
  const base = (device.onvif?.name || device.brand_hint || "nvr")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${base || "nvr"}-${device.ip.split(".").pop()}`;
}

type ClaimDraft = { serial: string; claim_code: string; store: string; name: string };
type AssignDraft = { connector: Connector; store: string; name: string };
type CustomerDraft = { connector: Connector; customer: string };

export function ConnectorsSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { scope, stores } = useScope();
  const list = useQuery({
    queryKey: ["connectors"],
    queryFn: api.connectors.list,
    refetchInterval: 10_000,
  });
  const [claimDraft, setClaimDraft] = useState<ClaimDraft | null>(null);
  const [assignDraft, setAssignDraft] = useState<AssignDraft | null>(null);
  const [customerDraft, setCustomerDraft] = useState<CustomerDraft | null>(null);
  const customers = useQuery({ queryKey: ["customers"], queryFn: api.customers.list });
  const [creating, setCreating] = useState<{ name: string; store: string } | null>(null);
  const [bundle, setBundle] = useState<EnrollmentBundle | null>(null);
  const [recorderDraft, setRecorderDraft] = useState<NvrDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const storeNames = stores.map((s) => s.name);
  const defaultStore = scope.kind === "store" ? scope.store : "";

  useEffect(() => {
    const pending = takePendingClaim();
    if (pending && canEdit) {
      setClaimDraft({ ...pending, store: defaultStore || storeNames[0] || "", name: "" });
    }
    // Only once, when the section opens from a label's QR link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiError ? e.message : fallback);
  const refresh = () => qc.invalidateQueries({ queryKey: ["connectors"] });
  const done = () => {
    setError(null);
    refresh();
  };

  const claim = useMutation({
    mutationFn: api.connectors.claim,
    onSuccess: () => {
      setClaimDraft(null);
      done();
    },
    onError: fail("Could not claim the box."),
  });
  const assign = useMutation({
    mutationFn: (d: AssignDraft) =>
      api.connectors.assign(d.connector.id, { store: d.store, name: d.name }),
    onSuccess: () => {
      setAssignDraft(null);
      done();
    },
    onError: fail("Could not assign the box."),
  });
  const setCustomer = useMutation({
    mutationFn: (d: CustomerDraft) =>
      api.connectors.setCustomer(d.connector.id, d.customer ? Number(d.customer) : null),
    onSuccess: () => {
      setCustomerDraft(null);
      done();
    },
    onError: fail("Could not change the box's customer."),
  });
  const release = useMutation({
    mutationFn: api.connectors.release,
    onSuccess: done,
    onError: fail("Could not return the box to stock."),
  });
  const create = useMutation({
    mutationFn: api.connectors.create,
    onSuccess: (res) => {
      setCreating(null);
      setBundle(res.bundle);
      done();
    },
    onError: fail("Could not add the connector."),
  });
  const reissue = useMutation({
    mutationFn: api.connectors.reissue,
    onSuccess: (b) => {
      setBundle(b);
      done();
    },
    onError: fail("Could not issue an enrollment bundle."),
  });
  const discover = useMutation({
    mutationFn: api.connectors.discover,
    onSuccess: refresh,
    onError: fail("Could not request a network scan."),
  });
  const del = useMutation({
    mutationFn: api.connectors.delete,
    onSuccess: (res) => {
      setError(res.warning || null);
      refresh();
    },
    onError: fail("Could not delete the connector."),
  });
  const saveRecorder = useNvrUpsert({
    onSuccess: () => {
      setRecorderDraft(null);
      setError(null);
    },
    onError: setError,
  });

  const all = list.data ?? [];
  const placed = all.filter((c) => c.store && (!defaultStore || c.store === defaultStore));
  const stock = all.filter((c) => !c.store);

  return (
    <SettingsSection
      title="Connectors"
      description="Small boxes at a store that link its recorders to this system."
      hint={
        <>
          A connector sits on the store network and keeps an outbound,
          encrypted link to this system, so the store needs no port
          forwarding or public address. It finds recorders on its network and
          forwards their streams; no video is processed on it. Boxes arrive
          enrolled and appear under In stock once plugged in; claim one with
          the serial and claim code on its label.
        </>
      }
      actions={
        canEdit && (
          <div className="flex items-center gap-3">
            <button
              className="text-xs text-bf-muted hover:text-bf-text disabled:opacity-40"
              onClick={() => setCreating({ name: "", store: defaultStore || storeNames[0] || "" })}
              disabled={storeNames.length === 0}
            >
              Enroll a device by file
            </button>
            <button
              className="bf-button disabled:opacity-40"
              onClick={() =>
                setClaimDraft({
                  serial: "",
                  claim_code: "",
                  store: defaultStore || storeNames[0] || "",
                  name: "",
                })
              }
              disabled={storeNames.length === 0}
              title={storeNames.length === 0 ? "Create a store first" : undefined}
            >
              <PackageCheck size={16} className="mr-2" /> Claim a box
            </button>
          </div>
        )
      }
    >
      {error && <Note tone="error">{error}</Note>}

      {placed.length === 0 ? (
        <EmptyState
          icon={Router}
          title={defaultStore ? `No connectors at “${defaultStore}”` : "No connectors at stores yet"}
          hint="Stores whose recorders this system can reach directly do not need one."
        />
      ) : (
        <ConnectorTable
          connectors={placed}
          canEdit={canEdit}
          actions={(c) => (
            <>
              <IconButton
                label={`Scan ${c.id}'s network`}
                title="Scan the store network"
                disabled={!c.registered || c.discover_requested}
                onClick={() => discover.mutate(c.id)}
              >
                <Radar size={15} />
              </IconButton>
              {!c.serial && (
                <IconButton
                  label={`New enrollment file for ${c.id}`}
                  title="New enrollment file"
                  onClick={() => reissue.mutate(c.id)}
                >
                  <RefreshCw size={15} />
                </IconButton>
              )}
              {c.serial && (
                <IconButton
                  label={`Return ${c.serial} to stock`}
                  title="Return to stock"
                  onClick={() => release.mutate(c.id)}
                >
                  <Undo2 size={15} />
                </IconButton>
              )}
              <IconButton danger label={`Delete ${c.id}`} title="Delete" onClick={() => del.mutate(c.id)}>
                <Trash2 size={15} />
              </IconButton>
            </>
          )}
        />
      )}

      {stock.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs uppercase tracking-wide text-bf-muted">In stock</h4>
          <ConnectorTable
            connectors={stock}
            canEdit={canEdit}
            actions={(c) => (
              <>
                <button
                  className="text-xs text-bf-muted hover:text-bf-text disabled:opacity-40"
                  disabled={storeNames.length === 0}
                  onClick={() =>
                    setAssignDraft({ connector: c, store: defaultStore || storeNames[0] || "", name: "" })
                  }
                >
                  Assign to store
                </button>
                <button
                  className="text-xs text-bf-muted hover:text-bf-text"
                  onClick={() =>
                    setCustomerDraft({
                      connector: c,
                      customer: c.customer_id == null ? "" : String(c.customer_id),
                    })
                  }
                >
                  Customer
                </button>
                <IconButton danger label={`Delete ${c.id}`} title="Delete" onClick={() => del.mutate(c.id)}>
                  <Trash2 size={15} />
                </IconButton>
              </>
            )}
          />
        </div>
      )}

      {placed
        .filter((c) => c.discovered)
        .map((c) => (
          <DiscoveredList
            key={c.id}
            connector={c}
            canEdit={canEdit}
            onAdd={(d) =>
              setRecorderDraft({
                ...emptyNvr,
                id: recorderIdFor(d),
                name: d.onvif?.name || "",
                brand: BRAND_OPTIONS.some((b) => b.id === d.brand_hint)
                  ? (d.brand_hint as string)
                  : "generic",
                host: d.ip,
                port: d.rtsp_port || 554,
                store: c.store,
                connector_id: c.id,
              })
            }
          />
        ))}

      {claimDraft && (
        <Modal onClose={() => setClaimDraft(null)} labelledBy="connector-claim-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              claim.mutate(claimDraft);
            }}
          >
            <h3 id="connector-claim-title" className="text-lg font-medium text-white">
              Claim a box
            </h3>
            <p className="text-sm text-bf-muted">
              Serial and claim code are on the label on the box and on its
              quick-start card.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label>
                <span className="mb-1 block text-xs text-bf-muted">Serial</span>
                <input
                  className="bf-input font-mono text-xs uppercase"
                  value={claimDraft.serial}
                  onChange={(e) => setClaimDraft({ ...claimDraft, serial: e.target.value })}
                  placeholder="ISC-2610-0001"
                  required
                />
              </label>
              <label>
                <span className="mb-1 block text-xs text-bf-muted">Claim code</span>
                <input
                  className="bf-input font-mono text-xs uppercase"
                  value={claimDraft.claim_code}
                  onChange={(e) => setClaimDraft({ ...claimDraft, claim_code: e.target.value })}
                  placeholder="ABCD-EFGH"
                  autoComplete="off"
                  required
                />
              </label>
            </div>
            <StorePicker
              value={claimDraft.store}
              onChange={(v) => setClaimDraft({ ...claimDraft, store: v })}
            />
            <NameField value={claimDraft.name} onChange={(v) => setClaimDraft({ ...claimDraft, name: v })} />
            <DialogButtons
              onCancel={() => setClaimDraft(null)}
              pending={claim.isPending}
              disabled={!claimDraft.store}
              label="Claim"
            />
          </form>
        </Modal>
      )}

      {assignDraft && (
        <Modal onClose={() => setAssignDraft(null)} labelledBy="connector-assign-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              assign.mutate(assignDraft);
            }}
          >
            <h3 id="connector-assign-title" className="text-lg font-medium text-white">
              Assign {assignDraft.connector.serial || assignDraft.connector.id}
            </h3>
            <StorePicker
              value={assignDraft.store}
              onChange={(v) => setAssignDraft({ ...assignDraft, store: v })}
            />
            <NameField
              value={assignDraft.name}
              onChange={(v) => setAssignDraft({ ...assignDraft, name: v })}
            />
            <DialogButtons
              onCancel={() => setAssignDraft(null)}
              pending={assign.isPending}
              disabled={!assignDraft.store}
              label="Assign"
            />
          </form>
        </Modal>
      )}

      {customerDraft && (
        <Modal onClose={() => setCustomerDraft(null)} labelledBy="connector-customer-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              setCustomer.mutate(customerDraft);
            }}
          >
            <h3 id="connector-customer-title" className="text-lg font-medium text-white">
              Customer for {customerDraft.connector.serial || customerDraft.connector.id}
            </h3>
            <p className="text-sm text-bf-muted">
              The customer's owners can then place the box at one of their
              locations without its claim code.
            </p>
            <Select
              ariaLabel="Customer"
              value={customerDraft.customer}
              onChange={(v) => setCustomerDraft({ ...customerDraft, customer: v })}
              options={[
                { value: "", label: "None (IntraScene stock)" },
                ...(customers.data ?? []).map((c) => ({ value: String(c.id), label: c.name })),
              ]}
            />
            <DialogButtons
              onCancel={() => setCustomerDraft(null)}
              pending={setCustomer.isPending}
              label="Save"
            />
          </form>
        </Modal>
      )}

      {creating && (
        <Modal onClose={() => setCreating(null)} labelledBy="connector-new-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate(creating);
            }}
          >
            <h3 id="connector-new-title" className="text-lg font-medium text-white">
              Enroll a device by file
            </h3>
            <p className="text-sm text-bf-muted">
              For a device that was not provisioned as a stock box: this issues
              an enrollment file to copy onto it.
            </p>
            <NameField
              value={creating.name}
              onChange={(v) => setCreating({ ...creating, name: v })}
              required
            />
            <StorePicker
              value={creating.store}
              onChange={(v) => setCreating({ ...creating, store: v })}
            />
            <DialogButtons
              onCancel={() => setCreating(null)}
              pending={create.isPending}
              disabled={!creating.store}
              label="Issue enrollment file"
            />
          </form>
        </Modal>
      )}

      {bundle && <BundleDialog bundle={bundle} onClose={() => setBundle(null)} />}

      {recorderDraft && (
        <NvrDraftModal
          draft={recorderDraft}
          setDraft={setRecorderDraft}
          onSubmit={(n) => saveRecorder.mutate(n)}
          pending={saveRecorder.isPending}
        />
      )}
    </SettingsSection>
  );
}

function ConnectorTable({
  connectors,
  canEdit,
  actions,
}: {
  connectors: Connector[];
  canEdit: boolean;
  actions: (c: Connector) => React.ReactNode;
}) {
  return (
    <div className="bf-card overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
          <tr>
            <th className="p-3">Name</th>
            <th className="p-3">Store</th>
            <th className="p-3">Customer</th>
            <th className="p-3">Status</th>
            <th className="p-3">Device</th>
            <th className="p-3">Recorders</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {connectors.map((c) => {
            const st = statusOf(c);
            return (
              <tr key={c.id} className="border-t border-white/5 align-top">
                <td className="p-3">
                  <div className="font-medium">{c.name || c.serial || c.id}</div>
                  <div className="font-mono text-xs text-bf-muted">{c.serial || `ID: ${c.id}`}</div>
                </td>
                <td className="p-3">
                  {c.store ? (
                    <span className="rounded-full bg-bf-violet/20 px-2 py-0.5 text-xs text-bf-text">
                      {c.store}
                    </span>
                  ) : (
                    <span className="text-xs text-bf-muted">—</span>
                  )}
                </td>
                <td className="p-3 text-xs">
                  {c.customer_name || <span className="text-bf-muted">—</span>}
                </td>
                <td className="p-3 text-xs">
                  <div className={st.tone}>{st.label}</div>
                  <div className="text-bf-muted">
                    {c.registered ? `Seen ${relativeTime(c.last_seen_at)}` : ""}
                    {c.online && c.path ? ` · ${PATH_LABEL[c.path] ?? c.path}` : ""}
                  </div>
                  {c.overlay_ip && <div className="font-mono text-bf-muted">{c.overlay_ip}</div>}
                </td>
                <td className="p-3 text-xs text-bf-muted">
                  <div>{c.facts?.model || c.hardware?.model || "—"}</div>
                  {c.hardware?.mac && <div className="font-mono">MAC {c.hardware.mac}</div>}
                  {c.facts?.lan?.map((l) => (
                    <div key={l.name} className="font-mono">
                      {l.name} {l.ip}/{l.prefix}
                    </div>
                  ))}
                  {c.facts?.temp_c != null && <div>{c.facts.temp_c} °C</div>}
                  {c.version && <div>Agent {c.version}</div>}
                </td>
                <td className="p-3 text-xs tabular-nums text-bf-muted">{c.recorder_ids.length}</td>
                <td className="p-3 text-right">
                  {canEdit && <div className="flex items-center justify-end gap-3">{actions(c)}</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function IconButton({
  label,
  title,
  danger,
  disabled,
  onClick,
  children,
}: {
  label: string;
  title: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      className={`text-bf-muted disabled:opacity-40 ${danger ? "hover:text-red-400" : "hover:text-bf-text"}`}
      aria-label={label}
      title={title}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function StorePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { stores } = useScope();
  return (
    <div>
      <span className="mb-1 block text-xs text-bf-muted">Store</span>
      <SearchableSelect
        ariaLabel="Store"
        value={value}
        onChange={onChange}
        placeholder="Choose a store…"
        options={stores.map((s) => ({
          value: s.name,
          label: s.display_name || s.name,
          hint: s.location || undefined,
        }))}
      />
    </div>
  );
}

function NameField({
  value,
  onChange,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-bf-muted">Name</span>
      <input
        className="bf-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Back office connector"
        required={required}
      />
    </label>
  );
}

function DialogButtons({
  onCancel,
  pending,
  disabled,
  label,
}: {
  onCancel: () => void;
  pending: boolean;
  disabled?: boolean;
  label: string;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" className="bf-button bg-white/10 bg-none" onClick={onCancel}>
        Cancel
      </button>
      <button className="bf-button disabled:opacity-40" type="submit" disabled={pending || disabled}>
        {pending ? "Saving…" : label}
      </button>
    </div>
  );
}

function DiscoveredList({
  connector,
  canEdit,
  onAdd,
}: {
  connector: Connector;
  canEdit: boolean;
  onAdd: (d: DiscoveredDevice) => void;
}) {
  const scan = connector.discovered!;
  return (
    <div className="bf-card space-y-2 p-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-3 pt-3">
        <h4 className="text-sm font-medium text-white">
          Found on {connector.name || connector.id}’s network
        </h4>
        <span className="text-xs text-bf-muted">
          {scan.networks.join(", ")} · scanned{" "}
          {relativeTime(new Date(scan.scanned_at * 1000).toISOString())}
          {connector.discover_requested ? " · new scan pending" : ""}
        </span>
      </div>
      {scan.devices.length === 0 ? (
        <p className="px-3 pb-3 text-sm text-bf-muted">
          No recorders or cameras answered on this network.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
            <tr>
              <th className="p-3">Address</th>
              <th className="p-3">Device</th>
              <th className="p-3">Answers on</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {scan.devices.map((d) => (
              <tr key={d.ip} className="border-t border-white/5">
                <td className="p-3 font-mono text-xs">{d.ip}</td>
                <td className="p-3 text-xs">
                  <div>{d.onvif?.name || d.onvif?.hardware || d.rtsp_server || "Unknown"}</div>
                  <div className="capitalize text-bf-muted">
                    {[d.brand_hint, d.onvif?.type].filter(Boolean).join(" · ")}
                  </div>
                </td>
                <td className="p-3 text-xs text-bf-muted">
                  {[d.onvif ? "ONVIF" : "", d.rtsp_port ? `RTSP :${d.rtsp_port}` : ""]
                    .filter(Boolean)
                    .join(" · ")}
                </td>
                <td className="p-3 text-right">
                  {canEdit && d.rtsp_port && (
                    <button
                      className="text-xs text-bf-muted hover:text-bf-text"
                      onClick={() => onAdd(d)}
                    >
                      <Plus size={13} className="mr-1 inline" />
                      Add as recorder
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BundleDialog({ bundle, onClose }: { bundle: EnrollmentBundle; onClose: () => void }) {
  const download = () => {
    const blob = new Blob([JSON.stringify(bundle, null, 2) + "\n"], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `enroll-${bundle.connector_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Modal onClose={onClose} labelledBy="bundle-title" panelClassName="w-full max-w-lg">
      <div className="space-y-3">
        <h3 id="bundle-title" className="text-lg font-medium text-white">
          Enrollment file for {bundle.connector_id}
        </h3>
        <p className="text-sm text-bf-muted">
          This file is shown once and enrolls one device. It is valid until{" "}
          {new Date(bundle.expires_at).toLocaleString()}. Copy it to the connector as{" "}
          <code>/etc/blinkfree-connector/enroll.json</code>, then restart the
          connector service; it appears here as Online within a minute.
        </p>
        <pre className="max-h-40 overflow-auto rounded-lg bg-black/40 p-3 font-mono text-xs text-bf-muted">
          {`scp enroll-${bundle.connector_id}.json root@<device>:/etc/blinkfree-connector/enroll.json
ssh root@<device> systemctl restart blinkfree-connector`}
        </pre>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="bf-button bg-white/10 bg-none" onClick={onClose}>
            Done
          </button>
          <button type="button" className="bf-button" onClick={download}>
            <Download size={16} className="mr-2" /> Download enroll.json
          </button>
        </div>
      </div>
    </Modal>
  );
}
